import { Map } from 'immutable';
import { stringify } from 'query-string';

import Api from '../api';

import Deed from './model';
import {
  addAction,
  addMultipleAction,
  updateAction,
  removeAction,
  upcomingDeedsLoadedAction,
  pastDeedsLoadedAction,
} from './actions';

export default class DeedsApi {
  static fetchUpcoming(userId = 'me') {
    return Api.get(`api/deeds/${userId}/upcoming`).map((deeds) => [
      addMultipleAction(Map(deeds.map((deed) => [deed.id, new Deed(deed)]))),
      upcomingDeedsLoadedAction(userId),
    ]);
  }

  static fetchPast(userId = 'me') {
    return Api.get(`api/deeds/${userId}/past`).map((deeds) => [
      addMultipleAction(Map(deeds.map((deed) => [deed.id, new Deed(deed)]))),
      pastDeedsLoadedAction(userId),
    ]);
  }

  static fetchDeedsForRules(searchTerm?: string, selected?: string[], deedTypes?: string[]) {
    const params = new URLSearchParams();
    if (searchTerm) {
      params.append('search', searchTerm);
    }
    if (selected) {
      params.append('selected', selected.join(','));
    }
    if (deedTypes) {
      params.append('deedTypes', deedTypes.join(','));
    }
    return Api.get(`api/deeds/me/upcoming?${params.toString()}`);
  }

  static fetch(id: string) {
    return Api.get(`api/deeds/${id}`).map((deed) => addAction(new Deed(deed)));
  }

  static fetchInfo(id: string) {
    return Api.get(`api/deeds/${id}`);
  }

  static fetchExternalLinkFollows(id: string) {
    return Api.get(`api/deeds/${id}/externalLinkFollows`);
  }

  static create(data) {
    return Api.post('api/deeds', data).map((deed) => addAction(new Deed(deed)));
  }

  static update(id: string, data) {
    return Api.patch(`api/deeds/${id}`, data).map((deed) => updateAction(new Deed(deed)));
  }

  static setUserRoleStatus(deedId: string, userId: string, data) {
    return Api.post(`api/deeds/${deedId}/setUserRole/${userId}`, data).map((deed) =>
      updateAction(new Deed(deed))
    );
  }

  static delete(id: string) {
    return Api.delete(`api/deeds/${id}`).map(() => removeAction(id));
  }

  static emailAttendees(id: string, subject, message, fromName, fromEmail) {
    return Api.post(`api/deeds/${id}/emailAttendees`, {
      subject,
      message,
      fromName,
      fromEmail,
    });
  }

  static checkInAttendee(deedId: string, userId: string) {
    return Api.patch(`api/deeds/${deedId}/checkIn/${userId}`, {});
  }

  static completeMilestone(deedId: string, milestoneId: string) {
    return Api.post(`api/deeds/${deedId}/milestones/${milestoneId}`).map((deed) =>
      updateAction(new Deed(deed))
    );
  }

  static addAttendees(deedId: string, users, emails, roles?: string[]) {
    return Api.post(`api/deeds/${deedId}/addAttendees`, {
      users,
      emails,
      roles,
    }).map((deed) => updateAction(new Deed(deed)));
  }

  static removeAttendee(deedId: string, userId: string, roleIds?: string[]) {
    return Api.post(`api/deeds/unjoin/${userId}`, { id: deedId, roleIds }).map((deed) =>
      updateAction(new Deed(deed))
    );
  }

  static inviteAttendees(deedId: string, users, emails) {
    return Api.post(`api/deeds/${deedId}/invite`, { users, emails }).map((deed) =>
      updateAction(new Deed(deed))
    );
  }

  static removeInvitee(deedId: string, userId: string) {
    return Api.post(`api/deeds/${deedId}/uninvite/${userId}`).map((deed) =>
      updateAction(new Deed(deed))
    );
  }

  static fetchExternalDeed(id: string) {
    return Api.get(`api/deeds/${id}`).map((externalDeed) => new Deed(externalDeed));
  }

  static searchExternalDeeds(query) {
    return Api.get(
      `api/deeds/searchExternalDeeds?${stringify(query, {
        arrayFormat: 'comma',
        skipEmptyString: true,
        skipNull: true,
      })}`
    ).map((externalDeeds) => Map(externalDeeds.map((deed) => [deed.id, new Deed(deed)])));
  }
}
